import { LoaderFunction } from 'react-router-dom';
import { store } from '../../../../state/configureStore';
import { client } from '../../../../gql/client';
import { FETCH_ACTIONS } from '../../../../gql/queries/actions/actions';
import { refreshOrderFlags } from '../../../../gql/services/orderFlags';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { getCursor } from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { selectActionOrders } from '../../../../gql/selectors/actions';
import { AuthContextType } from '../../../../hooks/useAuth';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { FETCH_EXTERNAL_COMPANIES } from '../../../../gql/queries/actions/externalCompanies';
import { FETCH_USERS } from '../../../../gql/queries/users';
import { FETCH_MATERIALS } from '../../../../gql/queries/actions/materials';

export const SearchAndCreateHistoricalActionLoader: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
    void refreshOrderFlags(userData);
    try {
      const cursorActions = getCursor(
        tableIds.SEARCH_AND_CREATE_HISTORICAL_ACTIONS
      )(store.getState());
      const dispatch = store.dispatch;

      const roleActive = userData?.user?.user?.rolActive;

      // FIX A4: Si concessionId no está seteada, pre-setear si solo hay 1 concesión.
      let effectiveConcessionId = cursorActions?.concessionId;
      if (!effectiveConcessionId) {
        try {
          const {
            data: { concessions },
          } = await client.query({ query: GET_CONCESSIONS });
          if (concessions?.length === 1) {
            effectiveConcessionId = concessions[0].id;
            dispatch(
              setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_ACTIONS, {
                ...cursorActions,
                concessionId: effectiveConcessionId,
              })
            );
          }
        } catch {
          /* continue without concessionId */
        }
      }

      const { data } = await client.query({
        query: FETCH_ACTIONS,
        variables: {
          input: {
            take: cursorActions?.take || takeBase,
            cursor: cursorActions?.cursor || '',
            sortField: cursorActions?.sortField,
            orderBy: cursorActions?.orderBy || 'desc',
            concessionId: effectiveConcessionId,
            fromDate: cursorActions?.fromDate,
            toDate: cursorActions?.toDate,
            indexExpedition: cursorActions?.indexExpedition,
            indexType: cursorActions?.indexType,
            classification: cursorActions?.classification,
            predefinedOrderId: cursorActions?.predefinedOrderId,
            states: cursorActions?.states,
            userIds: cursorActions?.userIds,
            operatorIds: cursorActions?.operatorIds,
            teamIds: cursorActions?.teamIds,
            capitolId: cursorActions?.capitolId,
            subCapitolId: cursorActions?.subCapitolId,
            externalCompanyId: cursorActions?.externalCompanyId,
            pkInitKm: cursorActions?.pkInitKm,
            pkInitMeter: cursorActions?.pkInitMeter,
            pkEndKm: cursorActions?.pkEndKm,
            pkEndMeter: cursorActions?.pkEndMeter,
            description: cursorActions?.description,
            laneCutting: cursorActions?.laneCutting,
            materialIds: cursorActions?.materialIds,
            hasMaterials: cursorActions?.hasMaterials,
            track: cursorActions?.track,
            activityId: cursorActions?.activityId,
            // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
            onlyFlagged: cursorActions?.onlyFlagged,
          },
        },
        fetchPolicy: 'cache-first',
      });

      const actions = await selectActionOrders(data.actions.actions);

      const [
        {
          data: { capitols },
        },
        {
          data: { concessions },
        },
        {
          data: { subCapitols },
        },
        {
          data: { classifications },
        },
        {
          data: { externalCompanies },
        },
        {
          data: { predefinedOrders },
        },
        {
          data: { users },
        },
      ] = await Promise.all([
        client.query({
          query: FETCH_CAPITOLS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: GET_CONCESSIONS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_SUB_CAPITOLS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_EXTERNAL_COMPANIES,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_USERS,
          fetchPolicy: 'cache-first',
        }),
      ]);

      const filtersDataOptions = {
        // Opciones del filtro de materiales. Sale del catálogo ya cacheado

        // (stale-while-revalidate, `cache-only` en la 1ª lectura), así que

        // sin cobertura no tumba el loader.

        materials: await client
          .query({ query: FETCH_MATERIALS })
          .then((r: any) => r.data?.materials ?? [])
          .catch(() => []),
        capitols,
        concessions,
        subCapitols,
        classifications,
        externalCompanies,
        predefinedOrders,
        users,
      };

      const combinedData = {
        actions,
        totalCount: data.actions.totalCount,
        pageInfo: data.actions.pageInfo,
        filtersDataOptions,
        roleActive,
      };

      return combinedData;
    } catch (err) {
      console.log('Loader offline fallback:', err);
      return {
        actions: [],
        totalCount: 0,
        pageInfo: { hasNextPage: false, hasPreviousPage: false },
        filtersDataOptions: {
          capitols: [],
          concessions: [],
          subCapitols: [],
          classifications: [],
          externalCompanies: [],
          predefinedOrders: [],
          users: [],
        },
        roleActive: userData?.user?.user?.rolActive,
      };
    }
  };

  return { data: load() };
};
